import { FilterItem, ReportTestItem } from '../utils/entities'
import sortByName from './sortByName';

const extractIdsAndTitles = (item: ReportTestItem) => {
  const { testSuiteId, category, subcategory, id: testSpecificationId, test_suite_title, label, type } = item.testSpecification;

  const isCompliance = type === 'compliance';

  const ids = {
    suite: testSuiteId,
    category: isCompliance ? `${testSuiteId}.${category}` : undefined,
    subcategory: isCompliance ? `${testSuiteId}.${category}.${subcategory}` : undefined,
    testSpecification: isCompliance 
      ? `${testSuiteId}.${category}.${subcategory}.${testSpecificationId.toString()}`
      : `${testSuiteId}.${testSpecificationId.toString()}`,
    testSpecificationId: testSpecificationId
  };

  const titles = {
    suite: test_suite_title,
    category: isCompliance ? category : undefined,
    subcategory: isCompliance ? subcategory : undefined,
    testSpecification: label,
    ratingType: item.ratingType
  };

  return { ids, titles, isCompliance };
}

const convertTestsToTestSuiteFilters = (testItems: ReportTestItem[]): FilterItem[] => {
  const filters = new Map<string, FilterItem>();

  // Single pass through test items
  for (const item of testItems) {
    // Create ids and titles in a more concise way
    const { ids, titles, isCompliance } = extractIdsAndTitles(item);

    if (isCompliance && ids.category && ids.subcategory && titles.category && titles.subcategory) {
      // Compliance test suites: Suite -> Category -> Subcategory -> Test Specification
      let suite = filters.get(ids.suite.toString());
      if (!suite) {
        suite = {
          key: 'testSuite',
          id: ids.suite.toString(),
          name: titles.suite,
          children: [],
          count: 0,
          extraProps: {
            ratingType: item.ratingType
          }
        };
        filters.set(ids.suite.toString(), suite);
      }
      suite.count++;

      let category = suite.children?.find(b => b.id === ids.category);
      if (!category) {
        category = {
          key: 'testSuite',
          id: ids.category,
          name: titles.category,
          children: [],
          count: 0
        };
        suite.children?.push(category);
      }
      category.count++;

      let subcategory = category.children?.find(l => l.id === ids.subcategory);
      if (!subcategory) {
        subcategory = {
          key: 'testSuite',
          id: ids.subcategory,
          name: titles.subcategory,
          children: [],
          count: 0
        };
        category.children?.push(subcategory);
      }
      subcategory.count++;

      let spec = subcategory.children?.find(a => a.id === ids.testSpecification);
      if (!spec) {
        spec = {
          key: 'testSuite',
          id: ids.testSpecification,
          name: titles.testSpecification,
          count: 0
        };
        subcategory.children?.push(spec);
      }
      spec.count++;
    } else {
      // Non-compliance test suites: Show Test Suite Name (condition/general have single specification)
      // Use specification ID for filtering, but display test suite name
      let spec = filters.get(ids.testSpecification);
      if (!spec) {
        spec = {
          key: 'testSuite',
          id: ids.testSpecification, // Keep specification ID for filter matching
          name: titles.suite, // Changed: Use test suite name instead of specification label
          count: 0,
          extraProps: {
            ratingType: item.ratingType
          }
        };
        filters.set(ids.testSpecification, spec);
      }
      spec.count++;
    }
  }
  
  return Array.from(filters.values())
    .sort(sortByName)
    .map(root => {
      // Sort all children recursively (handles both compliance and non-compliance hierarchies)
      if (root.children) {
        root.children.sort(sortByName);
        root.children.forEach(lvl1 => {
          if (lvl1.children) {
            lvl1.children.sort(sortByName);
            lvl1.children.forEach(lvl2 => {
              if (lvl2.children) {
                lvl2.children.sort(sortByName);
              }
            });
          }
        });
      }
      return root;
    });
};

export default convertTestsToTestSuiteFilters;
